// SiloLLMHeaderCard — top-of-Ops header card showing the silo's current
// Public Q&A LLM with a one-tap "Switch to Flash / Switch to Pro" toggle.
//
// May 25, 2026. Loads the silo's effective LLM via /silos/{id}/llm-effective
// and patches the silo's own routing override when the user toggles. Silo
// override takes precedence over creator default which takes precedence over
// platform default — so this card is the cleanest per-silo lever to flip
// Pro ↔ Flash.
//
// Jun 18, 2026 (Randy) — SAFETY LATCH. The toggle used to flip the model on a
// single tap with no confirmation, so it was easy to accidentally flip Public
// Q&A to the pricier Pro model. Now it opens a confirm dialog that also
// explains the Flash-vs-Pro trade-off. On confirm we also fire onChanged() so
// the per-surface tune panel below refetches and its OVERRIDE badge stays in
// sync (it used to go stale until a manual reload).

import { useEffect, useState } from "react";
import { Sparkle, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export default function SiloLLMHeaderCard({ siloId, siloName, onChanged }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = async () => {
    if (!siloId) return;
    try {
      const r = await api.get(`/silos/${siloId}/llm-effective`);
      setInfo(r.data);
    } catch (e) {
      // Silent — header is non-critical; rest of Ops still loads.
      // eslint-disable-next-line no-console
      console.warn("silo LLM effective fetch failed", e);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siloId]);

  if (!info) return null;

  const isPro = (info.model || "").toLowerCase().includes("pro");
  const isFlash = (info.model || "").toLowerCase().includes("flash");
  const targetModel = isPro ? "gemini-3.5-flash-lite" : "gemini-2.5-pro";
  const targetLabel = isPro ? "Flash" : "Pro";
  const switchingToPro = !isPro;

  const doSwitch = async () => {
    setBusy(true);
    try {
      await api.patch(`/silos/${siloId}/llm-routing`, {
        public_ask: { model: targetModel, provider: "gemini" },
      });
      toast.success(`Public Q&A switched to ${targetLabel}.`);
      setConfirmOpen(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch model.");
    } finally {
      setBusy(false);
    }
  };

  const flashTone = isFlash ? "bg-[#E6F2E6] border-[#2F6B3A] text-[#2F6B3A]" : "bg-[#FAF8F2] border-[#E5DDC5] text-[#1C1B1A]";

  return (
    <>
      {/* Iter160o (Randy pt-siloart-seagreen-encased) — the flashTone bg
          (#E6F2E6 when Flash active, #FAF8F2 when Pro active) isn't in
          the CSS exclusion chain. Marking the card `.siloart-encased`
          explicitly so its text stays native color under Silo Art V2. */}
      <div className={`border rounded-md p-3 sm:p-4 flex items-center gap-3 siloart-encased ${flashTone}`}
        data-testid="silo-llm-header-card">
        <div className="shrink-0 w-9 h-9 rounded-md bg-white/60 flex items-center justify-center">
          {isFlash ? <Lightning size={18} weight="duotone" /> : <Sparkle size={18} weight="duotone" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
            {siloName} · Public Q&A LLM
          </div>
          <div className="text-sm font-medium truncate" data-testid="silo-llm-header-model">
            {info.model}
            {isFlash && <span className="ml-1.5 text-[10px] uppercase tracking-[0.15em]">FREE</span>}
            {info.source && info.source !== "platform" && (
              <span className="ml-1.5 text-[9px] uppercase tracking-[0.18em] opacity-60">
                · {info.source === "silo" ? "silo override" : info.source === "creator" ? "your default" : "boost"}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={busy}
          data-testid="silo-llm-header-switch-btn"
          className="shrink-0 px-3 py-1.5 rounded-md bg-white border hairline text-xs uppercase tracking-[0.15em] hover:border-[#1C1B1A] disabled:opacity-50 flex items-center gap-1.5 text-[#1C1B1A]"
          title={`Switch this silo's Public Q&A to ${targetLabel}`}
        >
          <ArrowsClockwise size={12} /> {busy ? "Switching…" : `Switch to ${targetLabel}`}
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="silo-llm-switch-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Switch {siloName}'s Public Q&A to {targetLabel}?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              {switchingToPro ? (
                <span className="block">
                  <strong>Pro</strong> reasons harder on complex, multi-step questions and
                  nuanced writing — but it costs noticeably more per query and runs on paid
                  credit, not Gemini's free tier. Reach for it only when this silo genuinely
                  needs the extra depth.
                </span>
              ) : (
                <span className="block">
                  <strong>Flash</strong> isn't quite the deep reasoner Pro is on the very
                  hardest prompts — but it has excellent knowledge, a large context window,
                  and fast answers, and it taps Gemini's generous <strong>free tier</strong>.
                  For almost every silo it's the smart default and can save you big.
                </span>
              )}
              <span className="block text-[12px] text-[#6B6A68]">
                You're moving from <strong>{info.model}</strong> to{" "}
                <strong>{targetModel}</strong> for this silo's public answers only. You can
                switch back here anytime.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="silo-llm-switch-cancel" disabled={busy}>
              Keep {info.model.includes("flash") ? "Flash" : "Pro"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="silo-llm-switch-confirm-btn"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); doSwitch(); }}
            >
              {busy ? "Switching…" : `Yes, switch to ${targetLabel}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
